import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateProjectInput,
  CreateStepInput,
  CreateTaskInput,
  Event,
  Mutation,
  Project,
  ProjectFilters,
  PushOption,
  Step,
  Task,
  TaskFilters,
  UndoResult,
  UpdateProjectInput,
  UpdateStepInput,
  UpdateTaskInput,
  UserSummary,
} from "@tunic/shared";
import { api, qs } from "./client";
import { qk } from "./keys";

// ---- Projects ---------------------------------------------------------------

export const projectsQuery = (f: ProjectFilters = {}) =>
  queryOptions({ queryKey: qk.projects.list(f), queryFn: () => api.get<Project[]>(`/api/projects${qs(f)}`) });

export const projectQuery = (id: string) =>
  queryOptions({ queryKey: qk.projects.detail(id), queryFn: () => api.get<Project>(`/api/projects/${id}`) });

export const stepsQuery = (projectId: string) =>
  queryOptions({ queryKey: qk.projects.steps(projectId), queryFn: () => api.get<Step[]>(`/api/projects/${projectId}/steps`) });

export const projectTasksQuery = (projectId: string, f: TaskFilters = {}) =>
  queryOptions({
    queryKey: qk.projects.tasks(projectId, f),
    queryFn: () => api.get<Task[]>(`/api/projects/${projectId}/tasks${qs(f)}`),
  });

export const projectEventsQuery = (projectId: string) =>
  queryOptions({
    queryKey: qk.projects.events(projectId),
    queryFn: () => api.get<{ events: Event[]; next_cursor: string | null }>(`/api/projects/${projectId}/events?limit=100`),
  });

export const teamQuery = queryOptions({ queryKey: qk.team, queryFn: () => api.get<UserSummary[]>("/api/team"), staleTime: 60_000 });

export const useProjects = (f: ProjectFilters = {}) => useQuery(projectsQuery(f));
export const useProject = (id: string) => useQuery(projectQuery(id));
export const useSteps = (projectId: string) => useQuery(stepsQuery(projectId));
export const useProjectTasks = (projectId: string, f: TaskFilters = {}) => useQuery(projectTasksQuery(projectId, f));
export const useProjectEvents = (projectId: string) => useQuery(projectEventsQuery(projectId));
export const useTeam = () => useQuery(teamQuery);

/** Invalidate everything a project mutation could have touched. */
export function useInvalidateProject() {
  const qc = useQueryClient();
  return (projectId?: string | null) => {
    if (projectId) qc.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    qc.invalidateQueries({ queryKey: qk.projects.all });
    qc.invalidateQueries({ queryKey: qk.now });
    qc.invalidateQueries({ queryKey: qk.events.all });
  };
}

export function useCreateProject() {
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: (input: CreateProjectInput) => api.post<Mutation<Project>>("/api/projects", input),
    onSuccess: (m) => inv(m.data.id),
  });
}

export function useUpdateProject(id: string) {
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: (patch: UpdateProjectInput) => api.patch<Mutation<Project>>(`/api/projects/${id}`, patch),
    onSuccess: (m) => inv(m.data.id),
  });
}

// ---- Steps ------------------------------------------------------------------

export function useCreateStep(projectId: string) {
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: (input: CreateStepInput) => api.post<Mutation<Step>>(`/api/projects/${projectId}/steps`, input),
    onSuccess: () => inv(projectId),
  });
}

export function useUpdateStep(projectId: string) {
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateStepInput }) => api.patch<Mutation<Step>>(`/api/steps/${id}`, patch),
    onSuccess: () => inv(projectId),
  });
}

export function usePushStep(projectId: string) {
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: ({ id, option }: { id: string; option: PushOption }) => api.post<Mutation<Step>>(`/api/steps/${id}/push`, { option }),
    onSuccess: () => inv(projectId),
  });
}

export function useReorderSteps(projectId: string) {
  const qc = useQueryClient();
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: (ordered_ids: string[]) => api.patch<Mutation<Step[]>>(`/api/projects/${projectId}/steps/reorder`, { ordered_ids }),
    onMutate: async (ordered) => {
      await qc.cancelQueries({ queryKey: qk.projects.steps(projectId) });
      const prev = qc.getQueryData<Step[]>(qk.projects.steps(projectId));
      if (prev) {
        const byId = new Map(prev.map((s) => [s.id, s]));
        qc.setQueryData(
          qk.projects.steps(projectId),
          ordered.map((id, i) => ({ ...byId.get(id)!, position: i })),
        );
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(qk.projects.steps(projectId), ctx.prev),
    onSettled: () => inv(projectId),
  });
}

// ---- Tasks ------------------------------------------------------------------

export function useCreateTask(projectId: string) {
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: (input: Omit<CreateTaskInput, "project_id"> & { project_id?: string }) =>
      api.post<Mutation<Task>>("/api/tasks", { ...input, project_id: input.project_id ?? projectId }),
    onSuccess: () => inv(projectId),
  });
}

export function useUpdateTask(projectId: string) {
  const qc = useQueryClient();
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateTaskInput }) => api.patch<Mutation<Task>>(`/api/tasks/${id}`, patch),
    onMutate: async ({ id, patch }) => {
      // Optimistic for inline edits (status toggle, title, due). Rolled back on error.
      const key = qk.projects.tasks(projectId);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<Task[]>(key);
      if (prev) qc.setQueryData(key, prev.map((t) => (t.id === id ? { ...t, ...(patch as Partial<Task>) } : t)));
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(qk.projects.tasks(projectId), ctx.prev),
    onSettled: () => inv(projectId),
  });
}

export function usePushTask(projectId: string) {
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: ({ id, option }: { id: string; option: PushOption }) => api.post<Mutation<Task>>(`/api/tasks/${id}/push`, { option }),
    onSuccess: () => inv(projectId),
  });
}

// ---- Undo -------------------------------------------------------------------

export function useUndo() {
  const inv = useInvalidateProject();
  return useMutation({
    mutationFn: (eventId: string) => api.post<Mutation<UndoResult>>(`/api/events/${eventId}/undo`),
    onSuccess: (m) => {
      const pids = new Set(m.data.affected.map((a) => a.project_id).filter(Boolean));
      if (pids.size === 0) inv(null);
      for (const p of pids) inv(p);
    },
  });
}
